import { createSelector } from 'reselect';
import { GaugeChartDto, NetWorthChartSeries, NetWorthTableData } from './MoneyGuideProTypes';
import { ChartZone, MgpGaugeChartData, TargetZone } from './components/MgpGaugeChart/MgpGaugeChart';
import { SummaryChartData } from './Summary/SummaryChart/SummaryChart';
import { MgpState } from './MoneyGuideProReducer';

const selectChartData = (data: GaugeChartDto): MgpGaugeChartData => {
  const zones: ChartZone[] = [
    {
      color: data.lowColor || '#9553a1',
      range: [0, data.targetRangeLow],
    },
    {
      color: data.middleColor || '#a4ce38',
      range: [data.targetRangeLow, data.targetRangeHigh],
    },
    {
      color: data.highColor || '#48ade3',
      range: [data.targetRangeHigh, 100],
    },
  ];

  const targetZone: TargetZone = {
    color: data.targetColor,
    status: data.targetStatus,
  };

  return {
    value: Math.round(data.probabilityOfSuccess),
    targetZone,
    zones,
  };
};

const selectSummaryChartsData = (summaryData): SummaryChartData[] => {
  return summaryData.monteCarloCalculations.map(calculationData => {
    const chartData = selectChartData(calculationData);

    const targetRange = {
      low: Math.round(calculationData.targetRangeLow),
      high: Math.round(calculationData.targetRangeHigh),
    };

    return {
      ...chartData,
      avgReturn: `${Math.round(calculationData.averageReturn)}%`,
      badTiming: `${Math.round(calculationData.badTiming)}%`,
      targetRange,
    };
  });
};

export const selectNetWorthChartData = (netWorthData): NetWorthChartSeries[] => {
  const { investmentAssets, otherAssets, liabilities, networth } = netWorthData;

  return [
    {
      title: 'Assets',
      values: [
        {
          name: investmentAssets.name,
          value: investmentAssets.totalValue,
          color: investmentAssets.color || '#238c2a',
        },
        {
          name: otherAssets.name,
          value: otherAssets.totalValue,
          color: otherAssets.color || '#21d12d',
        },
      ],
    },
    {
      title: 'Liabilities',
      values: [
        {
          name: liabilities.name,
          value: liabilities.totalValue,
          color: liabilities.color || '#ff0000',
        },
      ],
    },
    {
      title: 'Net Worth',
      values: [
        {
          name: 'Net Worth',
          value: networth.totalValue,
          color: networth.color || '#1b46c6',
        },
      ],
    },
  ];
};

export const selectNetWorthTableData = (netWorthData): NetWorthTableData => {
  const { investmentAssets, otherAssets, liabilities, networth } = netWorthData;

  const groups = [
    {
      title: investmentAssets.name,
      color: investmentAssets.color || '#238c2a',
      rows: investmentAssets.categories.map(({ name, totalValue }) => ({
        label: name,
        value: totalValue,
      })),
    },
    {
      title: otherAssets.name,
      color: otherAssets.color || '#21d12d',
      rows: otherAssets.categories.map(({ name, totalValue }) => ({
        label: name,
        value: totalValue,
      })),
    },
    {
      title: liabilities.name,
      color: liabilities.color || '#ff0000',
      rows: liabilities.categories.map(({ name, totalValue }) => ({
        label: name,
        value: totalValue,
      })),
    },
  ];

  const netWorth = {
    value: networth.totalValue,
    color: networth.color || '#1b46c6',
  };

  return {
    groups,
    netWorth,
  };
};

const moneyGuideProSelector = state => state.moneyGuidePro as MgpState;

export const summarySelector = createSelector(moneyGuideProSelector, ({ summary }) => {
  if (!summary) {
    return summary;
  }

  const chartsData = selectSummaryChartsData(summary);

  return {
    ...summary,
    chartsData,
  };
});

export const insuranceSelector = createSelector(
  moneyGuideProSelector,
  ({ insurance }) => insurance,
);

export const goalsSelector = createSelector(moneyGuideProSelector, ({ goals }) => {
  if (!goals) {
    return goals;
  }

  const chartData = selectChartData(goals);

  return {
    ...goals,
    chartData,
  };
});

export const netWorthSelector = createSelector(moneyGuideProSelector, ({ netWorth }) => {
  if (!netWorth) {
    return netWorth;
  }

  const chartData = selectNetWorthChartData(netWorth);
  const tableData = selectNetWorthTableData(netWorth);

  return {
    ...netWorth,
    chartData,
    tableData,
  };
});
